/** Reads trade history over HTTP and exposes live events with subscription ACKs for HTTP reconciliation. */
import { toAssetTypeParam } from "../asset-type.types";
import { BaseReader } from "../base-reader";
import {
  UserTradeHistoryRequestArgs,
  UserTradesSchema,
  UserTradesWsMessage,
  UserTradesWsMessageSchema,
} from "./user-trade-history.types";

export class UserTradeHistoryReader extends BaseReader {
  /**
   * Get the trade history for a given user
   * @param subAddr The subaccount address of the user to get trade history for
   * @param limit The number of trades to get (default: 10)
   * @param offset The offset for pagination (default: 0)
   * @returns The trade history for the given user
   */
  async getByAddr({
    subAddr,
    limit = 10,
    offset = 0,
    startTimestamp,
    endTimestamp,
    sortDir,
    assetType = "perp",
    fetchOptions,
  }: UserTradeHistoryRequestArgs) {
    const queryParams: Record<string, string> = {
      account: subAddr,
      limit: limit.toString(),
      offset: offset.toString(),
    };
    if (startTimestamp !== undefined) queryParams.start_timestamp = startTimestamp.toString();
    if (endTimestamp !== undefined) queryParams.end_timestamp = endTimestamp.toString();
    if (sortDir !== undefined) queryParams.sort_dir = sortDir;
    const assetTypeParam = toAssetTypeParam(assetType);
    if (assetTypeParam !== undefined) queryParams.asset_type = assetTypeParam;

    const response = await this.getRequest({
      schema: UserTradesSchema,
      url: `${this.deps.config.tradingHttpUrl}/api/v1/trade_history`,
      queryParams,
      options: fetchOptions,
    });

    return response.data;
  }

  /**
   * Subscribe to user trades updates
   * @param subAddr The subaccount address of the user to subscribe to
   * @param onData Callback function for received user trades data
   * @param onSubscribed Called after subscription acknowledgment; use it to reconcile non-replaying history over HTTP.
   * @returns A function to unsubscribe from the user trades updates
   */
  subscribeByAddr(
    subAddr: string,
    onData: (data: UserTradesWsMessage) => void,
    onSubscribed?: () => void,
  ) {
    const topic = `user_trades:${subAddr}`;

    return this.deps.ws.subscribe(topic, UserTradesWsMessageSchema, onData, onSubscribed);
  }
}
